/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Typography,
    MenuItem,
    TextField,
    Select,
    InputLabel,
    FormControl,
    InputAdornment,
    Alert,
    Tooltip,
    Zoom,
    ClickAwayListener,
    Popper,
    Grow,
    Paper,
    Stack,
    Divider,
    IconButton
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Iconify } from '..';
import { FormProvider, RHFTextField } from '../hook-form';
import { AssignTaskPopup } from '.';
import { AssignUserAvatar } from '../custom-avatar';
import useCustomSnackbar from '../../hooks/useCustomSnackbar';
import { useAssignTaskToggle } from '../../hooks';
import useGetCurrentUser from '../../hooks/useGetCurrentUser';
import {
    bulkReminderIntervalPresets,
    bulkReminderGroupConst,
    bulkReminderPriorityConst
} from '../../constants/AddTaskReminder';
import { useSelector, dispatch } from '../../redux/store';
import { bulkCreateReminders, selectLeads } from '../../redux/slices/lead';
import { clearPracticeUserData } from '../../redux/slices/practiceUser';
import { bulkReminderSchema } from '../../validations/commonValidations';
import { getAssignees } from '../../utils/multiAsignees';
import {
    taskUserAvatar,
    openTask,
    assignTaskHandlerStyle,
    assignUserAvatarStyle,
    assignLengthStyle,
} from '../../styles/leadTaskReminder';
import { taskGroupStyle } from '../../styles/AddReminderStyle';

const fieldStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        bgcolor: '#f9fafb',
        '& fieldset': { borderColor: '#e5e8eb' },
        '&:hover fieldset': { borderColor: '#00A76F' },
        '&.Mui-focused fieldset': { borderColor: '#00A76F' }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#00A76F' }
};

const selectFieldStyle = {
    borderRadius: '10px',
    bgcolor: '#f9fafb',
    '& fieldset': { borderColor: '#e5e8eb' },
    '&:hover fieldset': { borderColor: '#00A76F' },
    '&.Mui-focused fieldset': { borderColor: '#00A76F' }
};

const getDefaultStartTime = () => {
    const now = moment();
    const remainder = 15 - (now.minute() % 15);
    return now.add(remainder, 'minutes').seconds(0).milliseconds(0).toDate();
};

const METHOD_ICONS = {
    CALL: 'fluent:call-20-filled',
    EMAIL: 'eva:email-fill',
    SMS: 'bi:chat-left-dots-fill',
    WHATSAPP: 'ri:whatsapp-fill',
    OTHER: 'mdi:target',
};

const METHOD_COLORS = {
    CALL: '#00A76F',
    EMAIL: '#1890FF',
    SMS: '#7635DC',
    WHATSAPP: '#25D366',
    OTHER: '#919EAB',
};

const BulkReminderForm = forwardRef(({ onClose }, ref) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const { user: currentUser } = useGetCurrentUser();

    const [assignToState, setAssignToState] = useState([]);
    const [assignError, setAssignError] = useState('');
    const [intervalPreset, setIntervalPreset] = useState(15);
    const [anchorElAssign, setAnchorElAssign] = useState(null);

    const { toggle: openAssignTask, onOpen: onOpenAssignTask, onClose: onCloseAssignTask } = useAssignTaskToggle();

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { multiLead } = useSelector((state) => state.lead);
    const { reminderMethod } = useSelector((state) => state.schema);

    const leadCount = multiLead?.leadId?.length || 0;

    useEffect(() => {
        if (currentUser && assignToState.length === 0) {
            setAssignToState([currentUser]);
        }
    }, [currentUser]);

    useEffect(() => {
        if (assignToState?.length > 0) {
            setAssignError('');
        }
    }, [assignToState]);

    const defaultValues = {
        method: 'CALL',
        startDateTime: getDefaultStartTime(),
        intervalMinutes: 15,
        title: '',
        note: '',
        group: 'FOLLOW_UP',
        priority: 'NORMAL',
    };

    const methods = useForm({
        resolver: yupResolver(bulkReminderSchema),
        mode: 'onChange',
        defaultValues
    });

    const { control, handleSubmit, watch, setValue, formState: { errors } } = methods;

    const watchedMethod = watch('method');
    const watchedStartDateTime = watch('startDateTime');
    const watchedIntervalMinutes = watch('intervalMinutes');

    const previewSummary = useMemo(() => {
        if (!watchedStartDateTime || !watchedIntervalMinutes || leadCount === 0) return null;

        const method = watchedMethod || 'CALL';
        const startMoment = moment(watchedStartDateTime);
        const lastMoment = startMoment.clone().add((leadCount - 1) * watchedIntervalMinutes, 'minutes');

        return {
            count: leadCount,
            method,
            startFormatted: startMoment.format('ddd DD MMM YYYY [at] h:mm A'),
            lastFormatted: lastMoment.format('ddd DD MMM YYYY [at] h:mm A'),
            interval: watchedIntervalMinutes,
        };
    }, [leadCount, watchedMethod, watchedStartDateTime, watchedIntervalMinutes]);

    const handleIntervalPresetChange = (value) => {
        setIntervalPreset(value);
        if (value !== 'custom') {
            setValue('intervalMinutes', value);
        }
    };

    const onSubmit = (data) => {
        if (leadCount > 50) {
            enqueueSnackbar('Maximum 50 leads can be selected for bulk reminders', { variant: 'error' });
            return;
        }

        const payload = {
            lead_ids: multiLead.leadId,
            start_datetime: moment(data.startDateTime).toISOString(),
            interval_minutes: Number(data.intervalMinutes),
            method: data.method,
            title: data.title || undefined,
            note: data.note || undefined,
            group: data.group || undefined,
            priority: data.priority || undefined,
            assignees: assignToState?.map((el) => el?.id),
        };

        const count = leadCount;

        // Close modal immediately
        if (onClose) onClose();

        // Show in-progress snackbar
        enqueueSnackbar(`Creating ${count} reminders...`, { variant: 'info', anchorOrigin: { vertical: 'top', horizontal: 'center' } });

        // Clear selections
        dispatch(selectLeads({ leadId: [], type: '', loading: false }));

        // Dispatch API call — callback fires after API completes
        dispatch(bulkCreateReminders(practiceId, payload, handleSuccess));
    };

    const handleSuccess = (result) => {
        if (result.success) {
            const count = result.data?.length || leadCount;
            enqueueSnackbar(`${count} reminders created successfully`, { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } else {
            const errorMsg = typeof result.error === 'object' ? JSON.stringify(result.error) : (result.error || 'Failed to create reminders');
            enqueueSnackbar(errorMsg, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        }
    };

    useImperativeHandle(ref, () => ({
        async submit() {
            if (assignToState?.length === 0) {
                setAssignError('At least one assignee is required');
                return;
            }
            setAssignError('');
            await handleSubmit(onSubmit)();
        },
    }));

    const assignTaskHandler = (e) => {
        e.stopPropagation();
        setAnchorElAssign(e.currentTarget);
        if (openAssignTask) {
            onCloseAssignTask();
            setAnchorElAssign(null);
            dispatch(clearPracticeUserData());
        } else {
            onOpenAssignTask();
        }
    };

    const onClickAwayEvent = () => {
        onCloseAssignTask();
        setAnchorElAssign(null);
        dispatch(clearPracticeUserData());
    };

    const methodOptions = reminderMethod?.length > 0
        ? reminderMethod
        : [
            { icon: 'fluent:call-20-filled', label: 'Call', value: 'CALL' },
            { icon: 'eva:email-fill', label: 'Email', value: 'EMAIL' },
            { icon: 'bi:chat-left-dots-fill', label: 'SMS', value: 'SMS' },
            { icon: 'ri:whatsapp-fill', label: 'WhatsApp', value: 'WHATSAPP' },
            { label: 'Other', value: 'OTHER' },
        ];

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box>
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '10px',
                        bgcolor: 'rgba(0, 167, 111, 0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Iconify icon="mdi:bell-plus" width={22} sx={{ color: '#00A76F' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            Bulk Reminder
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Create staggered reminders for {leadCount} selected lead{leadCount !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Stack>

                {leadCount > 50 && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                        Maximum 50 leads allowed. Please deselect {leadCount - 50} lead{leadCount - 50 !== 1 ? 's' : ''}.
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Method selection as icon buttons */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#637381', mb: 1 }}>Method</Typography>
                    <Controller
                        name="method"
                        control={control}
                        render={({ field }) => (
                            <Stack direction="row" spacing={1}>
                                {methodOptions?.map((option) => {
                                    const isSelected = field.value === option.value;
                                    const iconName = option.icon || METHOD_ICONS[option.value] || 'mdi:target';
                                    const color = METHOD_COLORS[option.value] || '#919EAB';
                                    return (
                                        <Box
                                            key={option.value}
                                            onClick={() => field.onChange(option.value)}
                                            sx={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                                                cursor: 'pointer', px: 1.5, py: 1, borderRadius: '10px',
                                                border: isSelected ? `2px solid ${color}` : '2px solid transparent',
                                                bgcolor: isSelected ? `${color}12` : '#f9fafb',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { bgcolor: `${color}12`, borderColor: `${color}60` },
                                                minWidth: 64,
                                            }}
                                        >
                                            <Iconify
                                                icon={iconName}
                                                width={22} height={22}
                                                sx={{ color: isSelected ? color : '#919EAB' }}
                                            />
                                            <Typography sx={{
                                                fontSize: '11px', fontWeight: isSelected ? 700 : 500,
                                                color: isSelected ? color : '#919EAB'
                                            }}>
                                                {option.label}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    />
                    {errors.method && (
                        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5 }}>
                            {errors.method.message}
                        </Typography>
                    )}
                </Box>

                {/* Scheduling section */}
                <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="startDateTime"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <DateTimePicker
                                    label="Start date & time"
                                    value={field.value || null}
                                    minDateTime={new Date()}
                                    format="dd/MM/yyyy HH:mm"
                                    ampm={false}
                                    onChange={(newValue) => field.onChange(newValue)}
                                    sx={fieldStyle}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            error: !!error,
                                            helperText: error?.message,
                                            onKeyDown: (e) => e.preventDefault()
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="bulk-reminder-interval-label">Interval</InputLabel>
                            <Select
                                labelId="bulk-reminder-interval-label"
                                label="Interval"
                                value={intervalPreset}
                                sx={selectFieldStyle}
                                onChange={(e) => handleIntervalPresetChange(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Iconify icon="mdi:timer-outline" width={20} height={20} sx={{ color: '#919EAB' }} />
                                    </InputAdornment>
                                }
                            >
                                {bulkReminderIntervalPresets.map((preset) => (
                                    <MenuItem key={preset.value} value={preset.value} sx={taskGroupStyle}>
                                        {preset.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>

                {intervalPreset === 'custom' && (
                    <Box sx={{ mb: 2.5 }}>
                        <Controller
                            name="intervalMinutes"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Custom interval (minutes)"
                                    error={!!error}
                                    helperText={error?.message || 'Enter a value between 1 and 1440'}
                                    InputProps={{
                                        inputProps: { min: 1, max: 1440 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="mdi:timer-outline" width={20} height={20} sx={{ color: '#919EAB' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldStyle}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        field.onChange(val);
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}

                {/* Title */}
                <Box sx={{ mb: 2.5 }}>
                    <RHFTextField
                        name="title"
                        label="Title (optional)"
                        size="small"
                        placeholder="e.g. Follow up call"
                        sx={fieldStyle}
                    />
                </Box>

                {/* Group + Priority side by side */}
                <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
                    <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="bulk-reminder-group-label">Group</InputLabel>
                        <Controller
                            name="group"
                            control={control}
                            render={({ field }) => (
                                <Select {...field} labelId="bulk-reminder-group-label" label="Group" sx={selectFieldStyle}>
                                    {bulkReminderGroupConst.map((option) => (
                                        <MenuItem key={option.value} value={option.value} sx={taskGroupStyle}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                    <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="bulk-reminder-priority-label">Priority</InputLabel>
                        <Controller
                            name="priority"
                            control={control}
                            render={({ field }) => (
                                <Select {...field} labelId="bulk-reminder-priority-label" label="Priority" sx={selectFieldStyle}>
                                    {bulkReminderPriorityConst.map((option) => (
                                        <MenuItem key={option.value} value={option.value} sx={taskGroupStyle}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </FormControl>
                </Stack>

                {/* Note */}
                <Box sx={{ mb: 2.5 }}>
                    <RHFTextField
                        name="note"
                        label="Note (optional)"
                        size="small"
                        multiline
                        rows={2}
                        placeholder="Add notes here"
                        sx={fieldStyle}
                    />
                </Box>

                {/* Assignees */}
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#637381', mb: 1 }}>Assignees</Typography>
                    <Box sx={{ cursor: 'pointer' }}>
                        {assignToState?.length > 0 ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={taskUserAvatar}>
                                    <Tooltip title={getAssignees({ assignees: assignToState })} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                        <Box sx={{ display: 'flex' }}>
                                            {assignToState?.slice(0, 3)?.map((assignee, index) => (
                                                <AssignUserAvatar
                                                    key={index}
                                                    sx={assignUserAvatarStyle}
                                                    src={assignee.user?.avatar || ''}
                                                    name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                                    role={assignee.role}
                                                    onClick={(e) => assignTaskHandler(e)}
                                                />
                                            ))}
                                            {assignToState?.length > 3 && (
                                                <Box sx={assignLengthStyle} onClick={(e) => assignTaskHandler(e)}>
                                                    + {assignToState?.length - 3}
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => assignTaskHandler(e)}
                                    sx={{ color: '#919EAB', '&:hover': { color: '#00A76F' } }}
                                >
                                    <Iconify icon="eva:edit-2-fill" width={16} />
                                </IconButton>
                            </Stack>
                        ) : (
                            <Box sx={openTask} onClick={(e) => e.stopPropagation()}>
                                <Box
                                    sx={{
                                        ...assignTaskHandlerStyle,
                                        bgcolor: '#f9fafb',
                                        border: '1.5px dashed #ddd',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: '#00A76F',
                                            bgcolor: 'rgba(0, 167, 111, 0.06)'
                                        }
                                    }}
                                    onClick={(e) => assignTaskHandler(e)}
                                >
                                    <Iconify sx={{ color: '#919EAB' }} width={20} height={20} icon="eva:plus-fill" />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {assignError && <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>{assignError}</Typography>}
                </Box>

                {/* Preview summary */}
                {previewSummary && leadCount <= 50 && (
                    <Alert
                        severity="info"
                        icon={<Iconify icon="mdi:calendar-clock" width={22} sx={{ color: '#1890FF' }} />}
                        sx={{
                            borderRadius: '10px',
                            bgcolor: 'rgba(24, 144, 255, 0.06)',
                            border: '1px solid rgba(24, 144, 255, 0.16)',
                            '& .MuiAlert-message': { width: '100%' },
                        }}
                    >
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                            Create <strong>{previewSummary.count} {previewSummary.method}</strong> reminder{previewSummary.count !== 1 ? 's' : ''} starting <strong>{previewSummary.startFormatted}</strong>, spaced <strong>{previewSummary.interval} minute{previewSummary.interval !== 1 ? 's' : ''}</strong> apart
                            {previewSummary.count > 1 && <> (last at <strong>{previewSummary.lastFormatted}</strong>)</>}
                        </Typography>
                    </Alert>
                )}
            </Box>

            {openAssignTask && (
                <ClickAwayListener onClickAway={onClickAwayEvent}>
                    <Popper open={openAssignTask} anchorEl={anchorElAssign} transition placement="bottom" sx={{ zIndex: '999999' }}>
                        {({ TransitionProps }) => (
                            <Grow {...TransitionProps}>
                                <Paper sx={{ borderRadius: 2 }}>
                                    <AssignTaskPopup
                                        open={openAssignTask}
                                        onClose={() => { onCloseAssignTask(); setAnchorElAssign(null); dispatch(clearPracticeUserData()); }}
                                        type="AddReminder"
                                        setAssignTo={setAssignToState}
                                        assignees={assignToState?.map((ele) => ele.id)}
                                    />
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </ClickAwayListener>
            )}
        </FormProvider>
    );
});

BulkReminderForm.propTypes = {
    onClose: PropTypes.func,
};

BulkReminderForm.displayName = 'BulkReminderForm';

export default BulkReminderForm;
